const FRAME_OFFSET_SECONDS = 0.05;
const MEDIA_TIMEOUT_MS = 4000;

const waitForMediaEvent = (element, eventName, timeoutMs = MEDIA_TIMEOUT_MS) =>
  new Promise((resolve, reject) => {
    const timer = globalThis.setTimeout(() => {
      cleanup();
      reject(new Error(`Timed out waiting for ${eventName}.`));
    }, timeoutMs);

    const cleanup = () => {
      globalThis.clearTimeout(timer);
      element.removeEventListener(eventName, handleEvent);
      element.removeEventListener("error", handleError);
    };
    const handleEvent = () => {
      cleanup();
      resolve();
    };
    const handleError = () => {
      cleanup();
      reject(new Error("The recorded video could not be decoded."));
    };

    element.addEventListener(eventName, handleEvent, { once: true });
    element.addEventListener("error", handleError, { once: true });
  });

const canvasToBlob = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("The video preview could not be created."));
      },
      "image/jpeg",
      0.92,
    );
  });

const drawVideoFrame = async (video, canvas) => {
  if (!video?.videoWidth || !video?.videoHeight) {
    throw new Error("The recorded video has no readable frame.");
  }

  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvasToBlob(canvas);
};

const resolveFiniteDuration = async (video) => {
  if (Number.isFinite(video.duration)) return video.duration;

  const durationChanged = waitForMediaEvent(video, "durationchange", 1500);
  video.currentTime = Number.MAX_SAFE_INTEGER;

  try {
    await durationChanged;
  } catch {
    // Some MediaRecorder WebM files do not expose a finite duration. The
    // caller can still use the live video element as a preview-only fallback.
  }

  return Number.isFinite(video.duration) ? video.duration : 0;
};

/**
 * 从已录视频末尾前 50ms 截一帧作为预览。
 * 部分浏览器生成的 WebM 没有可读 duration，因此允许回退到实时 video 当前帧。
 */
export const extractLastFrame = async (
  videoBlob,
  { canvas = document.createElement("canvas"), fallbackVideo } = {},
) => {
  if (!videoBlob) throw new Error("A recorded video is required.");

  const videoUrl = URL.createObjectURL(videoBlob);
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "metadata";

  try {
    video.src = videoUrl;
    video.load();
    await waitForMediaEvent(video, "loadedmetadata");

    const duration = await resolveFiniteDuration(video);
    if (!duration) {
      if (fallbackVideo) return drawVideoFrame(fallbackVideo, canvas);
      throw new Error("The recorded video duration is unavailable.");
    }

    const seeked = waitForMediaEvent(video, "seeked");
    video.currentTime = Math.max(duration - FRAME_OFFSET_SECONDS, 0);
    await seeked;
    return drawVideoFrame(video, canvas);
  } catch (error) {
    if (fallbackVideo?.videoWidth) {
      return drawVideoFrame(fallbackVideo, canvas);
    }
    throw error;
  } finally {
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(videoUrl);
  }
};
